import React from "react";
import { CheckCircle } from "@mui/icons-material";
import { IoCloseCircle } from "react-icons/io5";
import { IconButton, Tooltip, CircularProgress } from "@mui/material";
import { AiFillEdit, AiFillDelete } from "react-icons/ai";
import { FaEye } from "react-icons/fa";
import { MdOutlineMarkEmailUnread } from "react-icons/md";
import ConfirmDialog from "components/Modals/ConfirmDialog";

const ROLE_STYLES = {
  SUPER_ADMIN: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
  ADMIN: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300",
  ACCOUNT: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  USER: "bg-app-surface-muted text-app-text-secondary",
};

const RoleBadge = ({ role }) => {
  const style = ROLE_STYLES[role] || ROLE_STYLES.USER;
  return (
    <span
      className={`inline-flex rounded px-2 py-0.5 text-[11px] font-semibold uppercase ${style}`}
    >
      {role || "—"}
    </span>
  );
};

const UsersList = ({
  filteredUsers,
  navigate,
  setOpenConfirmationModal,
  searchText,
  resendInvite,
  isResendingInvite,
  canResend,
  orgId,
}) => {
  const [confirmId, setConfirmId] = React.useState(null);
  const wasResending = React.useRef(false);

  React.useEffect(() => {
    if (wasResending.current && !isResendingInvite) {
      setConfirmId(null);
    }
    wasResending.current = isResendingInvite;
  }, [isResendingInvite]);

  const sortedUsers = [...(filteredUsers || [])].sort((a, b) =>
    (a?.name || "").localeCompare(b?.name || "")
  );

  if (!sortedUsers.length) {
    return (
      <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <p className="text-sm font-medium text-app-text-secondary">
          {searchText ? "No users match your search" : "No users in this organization yet"}
        </p>
        <p className="mt-1 text-xs text-app-text-muted">
          {searchText ? "Try a different name, email, or username." : "Add a user to get started."}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-app-border bg-app-surface-muted/60">
              {["Name", "Username", "Email", "Contact", "Role", "Verified", "Actions"].map(
                (col) => (
                  <th
                    key={col}
                    className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-app-text-muted ${
                      col === "Actions" ? "text-center" : ""
                    }`}
                  >
                    {col}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((item) => (
              <tr
                key={item?.id}
                className="border-b border-app-border last:border-0 transition hover:bg-app-surface-muted/40"
              >
                <td className="px-4 py-3 font-medium text-app-text">{item?.name || "—"}</td>
                <td className="px-4 py-3 text-app-text-secondary">{item?.username || "—"}</td>
                <td className="px-4 py-3 text-app-text-secondary">{item?.email || "—"}</td>
                <td className="px-4 py-3 text-app-text-secondary">{item?.mobileNumber || "—"}</td>
                <td className="px-4 py-3">
                  <RoleBadge role={item?.type} />
                </td>
                <td className="px-4 py-3">
                  {item?.emailVerified === "Y" ? (
                    <CheckCircle className="text-emerald-600" fontSize="small" />
                  ) : (
                    <IoCloseCircle className="text-rose-500 text-lg" />
                  )}
                </td>
                <td className="px-4 py-3">
                  <UserActions
                    item={item}
                    navigate={navigate}
                    setOpenConfirmationModal={setOpenConfirmationModal}
                    setConfirmId={setConfirmId}
                    canResend={canResend}
                    isResendingInvite={isResendingInvite}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="divide-y divide-app-border md:hidden">
        {sortedUsers.map((item) => (
          <div key={item?.id} className="px-4 py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-medium text-app-text">{item?.name || "—"}</p>
                <p className="truncate text-xs text-app-text-muted">{item?.email}</p>
              </div>
              <RoleBadge role={item?.type} />
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-app-text-secondary">
              <div>
                <span className="text-app-text-muted">Username: </span>
                {item?.username || "—"}
              </div>
              <div>
                <span className="text-app-text-muted">Contact: </span>
                {item?.mobileNumber || "—"}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-app-text-muted">Verified: </span>
                {item?.emailVerified === "Y" ? (
                  <CheckCircle className="text-emerald-600" sx={{ fontSize: 16 }} />
                ) : (
                  <IoCloseCircle className="text-rose-500 text-base" />
                )}
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <UserActions
                item={item}
                navigate={navigate}
                setOpenConfirmationModal={setOpenConfirmationModal}
                setConfirmId={setConfirmId}
                canResend={canResend}
                isResendingInvite={isResendingInvite}
              />
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={confirmId != null}
        title="Resend invitation email?"
        message="A new temporary password will be generated and emailed to the user, replacing their current one. Continue?"
        confirmLabel="Resend"
        loading={isResendingInvite}
        onConfirm={() => resendInvite(confirmId)}
        onCancel={() => setConfirmId(null)}
      />
    </>
  );
};

const UserActions = ({
  item,
  navigate,
  setOpenConfirmationModal,
  setConfirmId,
  canResend,
  isResendingInvite,
}) => (
  <div className="flex items-center justify-center gap-1">
    <Tooltip title="Edit user" arrow>
      <IconButton
        size="small"
        onClick={() =>
          navigate(`/admin/user/edit/${item?.id}`, {
            state: { item },
          })
        }
      >
        <AiFillEdit className="text-primary" />
      </IconButton>
    </Tooltip>
    <Tooltip title="View details" arrow>
      <IconButton
        size="small"
        onClick={() =>
          navigate(`/admin/user/view/${item?.id}`, {
            state: { item },
          })
        }
      >
        <FaEye className="text-primary" />
      </IconButton>
    </Tooltip>
    {item?.emailVerified !== "Y" && canResend && (
      <Tooltip title="Resend invitation email" arrow>
        <span>
          <IconButton
            size="small"
            disabled={isResendingInvite}
            onClick={() => setConfirmId(item?.id)}
          >
            {isResendingInvite ? (
              <CircularProgress size={18} />
            ) : (
              <MdOutlineMarkEmailUnread className="text-primary" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    )}
    <Tooltip title="Delete user" arrow>
      <IconButton
        size="small"
        onClick={() =>
          setOpenConfirmationModal({
            state: true,
            id: item?.id,
          })
        }
      >
        <AiFillDelete className="text-rose-600" />
      </IconButton>
    </Tooltip>
  </div>
);

export default UsersList;
